import { useState } from 'react';
import { useSelector } from 'react-redux';
import { message } from 'antd';
import { analyzeSummarize, analyzeSummary } from '@api/viewerModule';
import { logger } from '@src/utils/misc/logger';
import { nexaUnifiedSelector } from '@store/common/selectors';

interface SummarizeRequest {
  diagnostic: unknown;
}

interface SummarizeResponse {
  status: string;
  error?: string;
  [key: string]: any;
}
interface UseAnalyzeSummarizeReturn {
  isLoading: boolean;
  data: SummarizeResponse | null;
  error: string | null;
  summarize: (diagnostic: SummarizeRequest['diagnostic']) => Promise<void>;
  reset: () => void;
}

const MAX_DIAGNOSTIC_TOKENS = 50000;
const APPROX_CHARS_PER_TOKEN = 4;
const MAX_DIAGNOSTIC_CHARS = MAX_DIAGNOSTIC_TOKENS * APPROX_CHARS_PER_TOKEN;
const MIN_STRING_CHARS = 64;

function isEmptyValue(value: any): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0 || value.every(isEmptyValue);
  if (typeof value === 'object') {
    const entries = Object.entries(value).filter(([, v]) => !isEmptyValue(v));
    return entries.length === 0;
  }
  return false;
}

function getSerializedLength(value: any) {
  try {
    return JSON.stringify(value)?.length ?? 0;
  } catch {
    return Number.MAX_SAFE_INTEGER;
  }
}

function trimStringFromMiddle(value: string, maxChars: number) {
  if (value.length <= maxChars) {
    return value;
  }

  if (maxChars <= 3) {
    return value.slice(0, maxChars);
  }

  const keepFromStart = Math.ceil((maxChars - 3) / 2);
  const keepFromEnd = Math.floor((maxChars - 3) / 2);
  return `${value.slice(0, keepFromStart)}...${value.slice(value.length - keepFromEnd)}`;
}

function trimArrayFromMiddle<T>(items: T[], maxItems: number) {
  if (items.length <= maxItems) {
    return items;
  }

  const keepFromStart = Math.ceil(maxItems / 2);
  const keepFromEnd = Math.floor(maxItems / 2);

  return [...items.slice(0, keepFromStart), ...items.slice(items.length - keepFromEnd)];
}

function removeEmptyDeep(value: any): any {
  if (value == null) {
    return value;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(removeEmptyDeep).filter((item) => !isEmptyValue(item));
  }

  if (typeof value === 'object') {
    return Object.entries(value).reduce(
      (acc, [key, nestedValue]) => {
        const cleanedValue = removeEmptyDeep(nestedValue);
        if (!isEmptyValue(cleanedValue)) {
          acc[key] = cleanedValue;
        }
        return acc;
      },
      {} as Record<string, any>,
    );
  }

  return value;
}

function trimValueToBudget(value: any, budgetChars: number): any {
  if (value == null) {
    return value;
  }

  const currentSize = getSerializedLength(value);
  if (currentSize <= budgetChars) {
    return value;
  }

  if (typeof value === 'string') {
    return trimStringFromMiddle(value, Math.max(MIN_STRING_CHARS, budgetChars));
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return value;
    }

    let keepCount = Math.max(1, Math.floor((value.length * budgetChars) / currentSize));
    let trimmedItems = trimArrayFromMiddle(value, keepCount);

    while (trimmedItems.length > 1 && getSerializedLength(trimmedItems) > budgetChars) {
      keepCount -= 1;
      trimmedItems = trimArrayFromMiddle(value, keepCount);
    }

    if (getSerializedLength(trimmedItems) <= budgetChars) {
      return trimmedItems;
    }

    const nestedBudget = Math.max(MIN_STRING_CHARS, Math.floor(budgetChars / trimmedItems.length));
    return trimmedItems
      .map((item) => trimValueToBudget(item, nestedBudget))
      .filter((item) => !isEmptyValue(item));
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (entries.length === 0) {
      return value;
    }

    let keepCount = Math.max(1, Math.floor((entries.length * budgetChars) / currentSize));
    let trimmedEntries = trimArrayFromMiddle(entries, keepCount);
    let trimmedObject = Object.fromEntries(trimmedEntries);

    while (trimmedEntries.length > 1 && getSerializedLength(trimmedObject) > budgetChars) {
      keepCount -= 1;
      trimmedEntries = trimArrayFromMiddle(entries, keepCount);
      trimmedObject = Object.fromEntries(trimmedEntries);
    }

    if (getSerializedLength(trimmedObject) <= budgetChars) {
      return trimmedObject;
    }

    const totalNestedSize = trimmedEntries.reduce(
      (sum, [, nestedValue]) => sum + Math.max(getSerializedLength(nestedValue), 1),
      0,
    );

    return Object.fromEntries(
      trimmedEntries
        .map(([key, nestedValue]) => {
          const nestedSize = Math.max(getSerializedLength(nestedValue), 1);
          const nestedBudget = Math.max(
            MIN_STRING_CHARS,
            Math.floor((budgetChars * nestedSize) / totalNestedSize),
          );
          return [key, trimValueToBudget(nestedValue, nestedBudget)];
        })
        .filter(([, nestedValue]) => !isEmptyValue(nestedValue)),
    );
  }

  return value;
}

function normalizeDiagnostic(diagnostic: any) {
  const cleanedDiagnostic = removeEmptyDeep(diagnostic);
  if (
    !cleanedDiagnostic ||
    typeof cleanedDiagnostic !== 'object' ||
    Array.isArray(cleanedDiagnostic) ||
    getSerializedLength(cleanedDiagnostic) <= MAX_DIAGNOSTIC_CHARS
  ) {
    return trimValueToBudget(cleanedDiagnostic, MAX_DIAGNOSTIC_CHARS);
  }

  const entries = Object.entries(cleanedDiagnostic);
  const totalNestedSize = entries.reduce(
    (sum, [, value]) => sum + Math.max(getSerializedLength(value), 1),
    0,
  );

  const normalizedDiagnostic = Object.fromEntries(
    entries.map(([key, value]) => {
      const nestedSize = Math.max(getSerializedLength(value), 1);
      const nestedBudget = Math.max(
        MIN_STRING_CHARS,
        Math.floor((MAX_DIAGNOSTIC_CHARS * nestedSize) / totalNestedSize),
      );
      return [key, trimValueToBudget(value, nestedBudget)];
    }),
  );

  return normalizedDiagnostic;
}

export const useAnalyzeSummarize = (): UseAnalyzeSummarizeReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<SummarizeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isNexaUnified = useSelector(nexaUnifiedSelector);

  const summarize = async (diagnostic: SummarizeRequest['diagnostic']) => {
    setIsLoading(true);
    setError(null);
    setData(null);

    try {
      const normalizedDiagnostic = normalizeDiagnostic(diagnostic);
      const response = isNexaUnified
        ? await analyzeSummary({ diagnostic: normalizedDiagnostic })
        : await analyzeSummarize({ diagnostic: normalizedDiagnostic });

      if (response?.data?.status === 'error') {
        const errorMsg = response?.data?.error || 'Failed to generate summary';
        setError(errorMsg);
        message.error(errorMsg);
        logger.error('Analyze summarize error:', response?.data);
      } else {
        setData(response?.data || null);
      }
    } catch (err: any) {
      const errorMsg = err?.response?.data?.error || err?.message || 'Failed to generate summary';
      setError(errorMsg);
      message.error(errorMsg);
      logger.error('Analyze summarize error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const reset = () => {
    setIsLoading(false);
    setData(null);
    setError(null);
  };

  return {
    isLoading,
    data,
    error,
    summarize,
    reset,
  };
};
